import { DropdownItemProps } from './DropdownList';
import { isEmpty, isEqual, isNil, isNumber, toLower } from 'lodash';
import { SyntheticEvent } from 'react';

export const setDefaultAutoSuggest = (placeholder: string, selectedItemLabel: string): string =>
  isEmpty(selectedItemLabel) || selectedItemLabel === placeholder ? '' : selectedItemLabel;

const findDownIndex = (curOption: number, items: DropdownItemProps[], hasPlaceholder: boolean): number => {
  for (let i = curOption + 1; i < items.length; i += 1) {
    if ((i === 0 && hasPlaceholder) || items[i].isDisabled) {
      continue;
    }
    return i;
  }
  return curOption;
};

const findUpIndex = (curOption: number, items: DropdownItemProps[], hasPlaceholder: boolean): number => {
  for (let i = curOption - 1; i >= 0; i -= 1) {
    if ((i === 0 && hasPlaceholder) || items[i].isDisabled) {
      continue;
    }
    return i;
  }
  return curOption;
};

const findNext = (curOption: number, items: DropdownItemProps[], isDownDirection: boolean, hasPlaceholder: boolean) => {
  let newOption;
  if (isDownDirection) {
    newOption = findDownIndex(curOption, items, hasPlaceholder);
  } else {
    newOption = findUpIndex(curOption, items, hasPlaceholder);
  }
  return newOption;
};

const isPlaceholderDisabled = (value: string): boolean => !isEqual(value, '-1') && !isEqual(value, '') && !isNil(value);

export const menuItems = (items: DropdownItemProps[] = [], placeholder: string, value: string): DropdownItemProps[] =>
  !isEmpty(placeholder)
    ? [
        {
          value: '',
          label: placeholder,
          isDisabled: isPlaceholderDisabled(value)
        },
        ...items
      ]
    : items;

export const getSelectedIndex = (items: DropdownItemProps[] = [], value: string, selectedIndex = 0) => {
  return !isNil(value) ? items.findIndex(x => toLower(x.value) === toLower(value) && !x.isDisabled) : selectedIndex;
};

const getShouldHandleEnterOrSpace = (isAutoSuggest, hasPlaceholder, browsedIndex) =>
  !isAutoSuggest || !hasPlaceholder || browsedIndex > 0;

const handleEnterOrSpace = (
  event: SyntheticEvent,
  selected: number,
  browsedIndex: number,
  handleChange: (e: SyntheticEvent, idx: number) => void,
  setExpanded: (val: boolean) => void,
  setBrowsedIndex: (index: number) => void,
  functionBooleans: {
    isExpanded: boolean;
    isAutoSuggest: boolean;
    hasPlaceholder: boolean;
  }
) => {
  const { isExpanded, isAutoSuggest, hasPlaceholder } = functionBooleans;
  setExpanded(!isExpanded);
  if (getShouldHandleEnterOrSpace(isAutoSuggest, hasPlaceholder, browsedIndex)) {
    if (isExpanded) {
      handleChange(event, browsedIndex);
    } else {
      setBrowsedIndex(selected);
    }
  }
};

const handleArrow = (
  expandedArrowIndex: number,
  selected: number,
  isExpanded: boolean,
  setExpanded: (val: boolean) => void,
  setBrowsedIndex: (index: number) => void,
  hasPlaceholder: boolean
) => {
  let hasSelected = isNumber(selected);
  if (hasPlaceholder && selected === 0) {
    hasSelected = false;
  }
  const browsedValueOnArrow = hasSelected ? selected : -1;
  if (!isExpanded) {
    setExpanded(true);
    setBrowsedIndex(browsedValueOnArrow);
  } else {
    setBrowsedIndex(expandedArrowIndex);
  }
};

export const buildHandler = (
  event: SyntheticEvent,
  items: DropdownItemProps[],
  selected: number,
  browsedIndex: number,
  placeholder: string,
  dropdownFunctions: {
    handleChange: (e: SyntheticEvent, idx: number) => void;
    setExpanded: (val: boolean) => void;
    setBrowsedIndex: (index: number) => void;
  },
  functionBooleans: {
    isExpanded: boolean;
    isAutoSuggest: boolean;
  }
): {
  Escape: () => void;
  Enter: () => number | void;
  [' ']?: () => number | void;
  End: () => void;
  Home: () => void;
  ArrowUp: () => number;
  ArrowDown: () => number;
} => {
  const { handleChange, setExpanded, setBrowsedIndex } = dropdownFunctions;
  const { isExpanded, isAutoSuggest } = functionBooleans;
  const hasPlaceholder = !isEmpty(placeholder);
  const itemsLastIndex = items.length - 1;

  const getEnterOrSpaceFn = () => {
    handleEnterOrSpace(event, selected, browsedIndex, handleChange, setExpanded, setBrowsedIndex, {
      isExpanded,
      isAutoSuggest,
      hasPlaceholder
    });
    return browsedIndex;
  };

  const handlers = {
    Escape: () => setExpanded(false),
    Enter: getEnterOrSpaceFn,
    End: () => setBrowsedIndex(findNext(items.length, items, false, hasPlaceholder)),
    Home: () => setBrowsedIndex(findNext(-1, items, true, hasPlaceholder)),
    ArrowUp: () => {
      let newIndex;
      if (browsedIndex === 0) {
        newIndex = 0;
      } else if (browsedIndex === -1) {
        newIndex = itemsLastIndex;
      } else {
        newIndex = findNext(browsedIndex, items, false, hasPlaceholder);
      }
      handleArrow(newIndex, selected, isExpanded, setExpanded, setBrowsedIndex, hasPlaceholder);
      return newIndex;
    },
    ArrowDown: () => {
      let newIndex = 0; // Default value
      if (browsedIndex === itemsLastIndex) {
        newIndex = itemsLastIndex;
      } else {
        newIndex = findNext(browsedIndex, items, true, hasPlaceholder);
      }
      handleArrow(newIndex, selected, isExpanded, setExpanded, setBrowsedIndex, hasPlaceholder);
      return newIndex;
    }
  };

  if (isAutoSuggest) {
    return handlers;
  }
  return {
    ...handlers,
    ' ': getEnterOrSpaceFn
  };
};
